import { useEffect, useState, useMemo } from "react";
import { api, adminHeaders, PROPERTY_NAMES } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { format, startOfMonth, endOfMonth, subMonths } from "date-fns";
import { it } from "date-fns/locale";
import {
  Sun,
  MoonStar,
  Loader2,
  Search,
  History,
  Euro,
  Calendar as CalendarIcon,
} from "lucide-react";

export const HistoryTab = () => {
  const today = new Date();
  const [start, setStart] = useState(format(startOfMonth(subMonths(today, 3)), "yyyy-MM-dd"));
  const [end, setEnd] = useState(format(endOfMonth(today), "yyyy-MM-dd"));
  const [typeFilter, setTypeFilter] = useState("all");
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchHistory = async () => {
    setLoading(true);
    try {
      const params = { start, end, upcoming: false };
      if (typeFilter !== "all") params.booking_type = typeFilter;
      const res = await api.get("/admin/bookings", { ...adminHeaders(), params });
      setBookings(res.data);
    } catch (err) {
      toast.error("Errore caricamento storico");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHistory();
    // eslint-disable-next-line
  }, [start, end, typeFilter]);

  const stats = useMemo(() => {
    const total = bookings.reduce((s, b) => s + (b.price || 0), 0);
    const dayuse = bookings.filter((b) => b.booking_type === "dayuse");
    const overnight = bookings.filter((b) => b.booking_type === "overnight");
    return {
      count: bookings.length,
      total: total.toFixed(2),
      dayuse: dayuse.length,
      overnight: overnight.length,
      dayuse_total: dayuse.reduce((s, b) => s + (b.price || 0), 0).toFixed(2),
      overnight_total: overnight.reduce((s, b) => s + (b.price || 0), 0).toFixed(2),
    };
  }, [bookings]);

  const setPreset = (months) => {
    const d = new Date();
    setStart(format(startOfMonth(subMonths(d, months - 1)), "yyyy-MM-dd"));
    setEnd(format(endOfMonth(d), "yyyy-MM-dd"));
  };

  return (
    <div data-testid="history-tab" className="space-y-6">
      {/* Filters */}
      <div className="bg-white rounded-2xl border border-border p-6">
        <div className="flex items-center gap-2 mb-4">
          <History className="w-5 h-5 text-primary" />
          <div className="font-display text-xl text-primary">Filtro storico</div>
        </div>
        <div className="grid md:grid-cols-4 gap-4">
          <div>
            <Label className="text-xs uppercase tracking-widest text-muted-foreground">
              Dal
            </Label>
            <Input
              type="date"
              value={start}
              onChange={(e) => setStart(e.target.value)}
              className="mt-2"
              data-testid="history-start"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-muted-foreground">
              Al
            </Label>
            <Input
              type="date"
              value={end}
              onChange={(e) => setEnd(e.target.value)}
              className="mt-2"
              data-testid="history-end"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-muted-foreground">
              Tipo
            </Label>
            <div className="mt-2 grid grid-cols-3 gap-1">
              {[
                { v: "all", l: "Tutti" },
                { v: "dayuse", l: "DayUse" },
                { v: "overnight", l: "Notti" },
              ].map((opt) => (
                <button
                  key={opt.v}
                  onClick={() => setTypeFilter(opt.v)}
                  data-testid={`history-type-${opt.v}`}
                  className={`py-2 rounded text-xs font-medium border ${
                    typeFilter === opt.v
                      ? "bg-primary text-primary-foreground border-primary"
                      : "bg-white border-border hover:border-primary"
                  }`}
                >
                  {opt.l}
                </button>
              ))}
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-muted-foreground">
              Periodi rapidi
            </Label>
            <div className="mt-2 flex gap-1">
              <Button
                onClick={() => setPreset(1)}
                size="sm"
                variant="outline"
                className="pill text-xs flex-1"
                data-testid="preset-1m"
              >
                1 mese
              </Button>
              <Button
                onClick={() => setPreset(3)}
                size="sm"
                variant="outline"
                className="pill text-xs flex-1"
                data-testid="preset-3m"
              >
                3 mesi
              </Button>
              <Button
                onClick={() => setPreset(12)}
                size="sm"
                variant="outline"
                className="pill text-xs flex-1"
                data-testid="preset-12m"
              >
                1 anno
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <SummaryCard
          label="Prenotazioni"
          value={stats.count}
          icon={<Search className="w-4 h-4" />}
        />
        <SummaryCard
          label="Incasso totale"
          value={`€${stats.total.replace(".", ",")}`}
          icon={<Euro className="w-4 h-4" />}
        />
        <SummaryCard
          label={`DayUse (${stats.dayuse})`}
          value={`€${stats.dayuse_total.replace(".", ",")}`}
          icon={<Sun className="w-4 h-4" />}
          accent="text-amber-600"
        />
        <SummaryCard
          label={`Pernotti (${stats.overnight})`}
          value={`€${stats.overnight_total.replace(".", ",")}`}
          icon={<MoonStar className="w-4 h-4" />}
        />
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-border overflow-hidden">
        <div className="p-4 flex items-center justify-between border-b border-border">
          <div className="font-display text-lg text-primary">
            {bookings.length} prenotazion{bookings.length === 1 ? "e" : "i"} nel periodo
          </div>
          {loading && <Loader2 className="w-4 h-4 animate-spin text-primary" />}
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-muted/60">
              <tr className="text-left">
                <th className="p-3">Riferimento</th>
                <th className="p-3">Tipo</th>
                <th className="p-3">Camera</th>
                <th className="p-3">Ospite</th>
                <th className="p-3">Data</th>
                <th className="p-3 text-right">€</th>
              </tr>
            </thead>
            <tbody>
              {bookings.map((b) => (
                <tr
                  key={b.id}
                  className="border-t border-border/60"
                  data-testid={`history-row-${b.id}`}
                >
                  <td className="p-3">
                    <span className="font-mono text-xs bg-muted px-2 py-1 rounded">
                      {b.reference || "—"}
                    </span>
                  </td>
                  <td className="p-3">
                    <span
                      className={`inline-flex items-center gap-1 pill px-2 py-1 text-[11px] font-semibold ${
                        b.booking_type === "dayuse"
                          ? "bg-amber-100 text-amber-800"
                          : "bg-primary/10 text-primary"
                      }`}
                    >
                      {b.booking_type === "dayuse" ? (
                        <Sun className="w-3 h-3" />
                      ) : (
                        <MoonStar className="w-3 h-3" />
                      )}
                      {b.booking_type === "dayuse" ? "DayUse" : "Notte"}
                    </span>
                  </td>
                  <td className="p-3">
                    <div className="font-medium">{b.room_name}</div>
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                      {PROPERTY_NAMES[b.property]}
                    </div>
                  </td>
                  <td className="p-3">
                    {b.guest_first_name} {b.guest_last_name}
                  </td>
                  <td className="p-3">
                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                      <CalendarIcon className="w-3 h-3" />
                      {b.booking_type === "dayuse" ? (
                        <>
                          {format(new Date(b.check_in), "d MMM yyyy", { locale: it })}
                          {" · "}
                          {b.slot_start}–{b.slot_end}
                        </>
                      ) : (
                        <>
                          {format(new Date(b.check_in), "d MMM", { locale: it })} →{" "}
                          {format(new Date(b.check_out), "d MMM yyyy", { locale: it })}
                        </>
                      )}
                    </div>
                  </td>
                  <td className="p-3 font-medium text-right">€{b.price}</td>
                </tr>
              ))}
              {bookings.length === 0 && !loading && (
                <tr>
                  <td colSpan={6} className="p-10 text-center text-muted-foreground">
                    Nessuna prenotazione nel periodo selezionato.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const SummaryCard = ({ label, value, icon, accent = "text-primary" }) => (
  <div className="bg-white rounded-2xl border border-border p-5">
    <div className="flex items-center justify-between mb-2">
      <span className="text-xs uppercase tracking-widest text-muted-foreground">
        {label}
      </span>
      <span className={accent}>{icon}</span>
    </div>
    <div className="font-display text-2xl text-primary">{value}</div>
  </div>
);
